import type { components } from '../api.generated';

type AppKind = components['schemas']['AppKind'];

/**
 * Log level definitions with their numeric values.
 * Higher values indicate more severe log levels.
 */
export const LOG_LEVELS = {
    DEBUG: 0,
    INFO: 10,
    WARN: 20,
    ERROR: 30,
    FATAL: 40,
    OFF: 50,
} as const;

export const LOG_LEVEL_MAPPER = {
    DEBUG: 'Debug',
    INFO: 'Info',
    WARN: 'Warn',
    ERROR: 'Error',
    FATAL: 'Fatal',
    OFF: null,
} as const;

/** Type representing the available log levels */
export type LogLevel = keyof typeof LOG_LEVELS;
/** Type representing the numeric values of log levels */
export type LogLevelValue = typeof LOG_LEVELS[LogLevel];

/** Type for log messages, which can be a single value or an array of values */
export type LogMessage = any | any[];

/**
 * Type definition for log metadata.
 * Contains optional fields for application and API context.
 */
export type LogMetadata = {
    /** The kind of application generating the log */
    appKind?: AppKind;
    /** The URL of the application */
    appUrl?: string;
    /** The API endpoint URL being called */
    apiUrl?: string;
    /** The HTTP method used for the API call */
    apiHttpMethod?: components['schemas']['HttpMethod'];
    /** Unique identifier for the API request */
    apiRequestId?: string;
    /** Unique identifier for the device */
    deviceId?: string;
    /** Name of the device */
    deviceName?: string;
    /** Network information for the device */
    deviceNetwork?: string;
    /** Project Version */
    projectVersionId?: string;
};

/**
 * Represents a single log entry's data.
 * Contains all information about a specific log event.
 */
export type LogEntry = {
    /** Timestamp when the log entry was created */
    timestamp: Date;
    /** The numeric log level value */
    level: LogLevelValue;
    /** Name of the logger instance that created this entry */
    loggerName: string;
    /** Optional version of the logger instance */
    version?: string;
    /** The actual data/messages being logged */
    messages: LogMessage;
    /** Optional metadata associated with this log entry */
    metadata?: LogMetadata;
};

/**
 * Interface for a transport mechanism (where logs are sent).
 * Implementations must provide a log method to process log entries.
 */
export type Transport = {
    /**
     * Processes and outputs a log entry.
     * @param entry The log entry data to be processed
     */
    log: (entry: LogEntry) => void;

    /**
     * Optional: The minimum level this transport should handle.
     * If not set, it respects the logger's level.
     */
    level?: LogLevel;
};

/**
 * Configuration options for creating a logger instance.
 * Defines the behavior and settings of a logger.
 */
export type LoggerOptions = {
    /** The name of the logger, used for context in output */
    name: string;

    /** The minimum log level to process. Messages below this level are ignored */
    level: LogLevel;

    /** An array of transports to send log entries to */
    transports: Transport[];

    /** Optional: A version string for the logger instance */
    version?: string;

    /** Optional: Default metadata to include with all log entries */
    metadata?: LogEntry['metadata'];
};

/**
 * The public interface of a logger instance.
 * Provides methods for logging at different levels and managing logger state.
 */
export type Logger = {
    /**
     * Logs messages at the DEBUG level.
     * @param messages Data/messages to log
     * @param metadata Optional metadata to include with this log entry
     */
    debug: (messages: LogMessage, metadata?: LogMetadata) => void;

    /**
     * Logs messages at the INFO level.
     * @param messages Data/messages to log
     * @param metadata Optional metadata to include with this log entry
     */
    info: (messages: LogMessage, metadata?: LogMetadata) => void;

    /**
     * Logs messages at the WARN level.
     * @param messages Data/messages to log
     * @param metadata Optional metadata to include with this log entry
     */
    warn: (messages: LogMessage, metadata?: LogMetadata) => void;

    /**
     * Logs messages at the ERROR level.
     * @param messages Data/messages to log (often includes Error objects)
     * @param metadata Optional metadata to include with this log entry
     */
    error: (messages: LogMessage, metadata?: LogMetadata) => void;

    /**
     * Changes the minimum log level for this logger instance dynamically.
     * @param level The new minimum log level
     */
    setLevel: (level: LogLevel) => void;

    /** Gets the current minimum log level for this logger instance */
    level: LogLevel;

    /** Gets the name of this logger instance */
    name: string;
};
